import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, CheckCircle2, Loader2, MessageCircle, RotateCcw, Send } from "lucide-react";
import { Badge } from "src/components/ui/Badge";
import { formatDateTime } from "src/utils/date-time";
import { SupportReply, SupportRequest } from "src/types/supportRequest.types";

// Grows with content up to this many lines, then scrolls internally —
// matches the "single line first, expand as you type" ask.
const COMPOSER_MIN_HEIGHT = 40;
const COMPOSER_MAX_HEIGHT = 120;

interface MailContentProps {
  request: SupportRequest | null;
  onReply?: (message: string) => void;
  replying?: boolean;
  replyResult?: SupportRequest | null;
  replyError?: string | null;
  viewerType?: "admin" | "tenant";
  /** Admin-only: close/reopen the ticket from within the conversation. */
  onStatusChange?: (status: "OPEN" | "CLOSED") => void;
  statusChanging?: boolean;
}

const CONTAINER_HEIGHT = "flex h-[68vh] max-h-[calc(85dvh-64px)] min-h-0 w-full flex-col text-gray-900 dark:text-gray-100";

const MailContent: React.FC<MailContentProps> = ({
  request, onReply, replying = false, replyResult, replyError, viewerType = "admin",
  onStatusChange, statusChanging = false,
}) => {
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<{ message: string; failed: boolean } | null>(null);
  // Freeze the boundary for this opening; sending a reply must not move it.
  const [readBoundary, setReadBoundary] = useState<number | null>(null);
  const unreadRef = useRef<HTMLDivElement>(null);
  const positionedRef = useRef(false);
  const nearBottomRef = useRef(true);
  useEffect(() => {
    if (request?.readMessageCount !== undefined) {
      setReadBoundary((current) => current ?? request.readMessageCount ?? 0);
    }
  }, [request?.readMessageCount]);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (replyResult && replyResult._id === request?._id) setPending(null);
    else if (replyError) setPending((current) => current ? { ...current, failed: true } : null);
  }, [replyResult, replyError, request?._id]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    if (!positionedRef.current && readBoundary !== null) {
      const unread = unreadRef.current;
      thread.scrollTop = unread
        ? unread.getBoundingClientRect().top - thread.getBoundingClientRect().top + thread.scrollTop - 16
        : thread.scrollHeight;
      positionedRef.current = true;
    } else if (pending || nearBottomRef.current) {
      thread.scrollTop = thread.scrollHeight;
    }
  }, [request?.replies?.length, pending, readBoundary]);

  // Auto-grow the composer with content, up to COMPOSER_MAX_HEIGHT, then scroll.
  // Layout effect (not a regular effect) so the resize applies before paint —
  // otherwise the box briefly renders at its old height on every keystroke.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, COMPOSER_MIN_HEIGHT), COMPOSER_MAX_HEIGHT)}px`;
  }, [draft]);

  if (!request) return (
    <div className={CONTAINER_HEIGHT}>
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="animate-spin" size={18} /> Loading conversation…
      </div>
    </div>
  );

  const contactForm = !request.tenantId;
  const internalReply = contactForm && viewerType === "admin";
  const status = request.status?.toLowerCase() || "open";
  const messages: SupportReply[] = [
    { authorType: "tenant", authorName: request.name, message: request.message, createdAt: request.createdAt || "" },
    ...(request.replies || []),
  ];
  const firstUnreadIndex = readBoundary === null ? -1 : messages.findIndex(
    (message, index) => index >= readBoundary && message.authorType !== viewerType
  );
  const send = (message: string) => {
    if (!message.trim() || !onReply || replying || (pending && !pending.failed)) return;
    setPending({ message: message.trim(), failed: false });
    setDraft("");
    onReply(message.trim());
    inputRef.current?.focus();
  };

  return (
    <div className={CONTAINER_HEIGHT}>
      <div className="flex shrink-0 items-center gap-2.5 border-b border-gray-200 bg-white px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800 sm:px-5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-gray-700"><MessageCircle size={16} /></div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold leading-tight">{viewerType === "admin" ? request.name : "Invyntra support"}</div>
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
            {contactForm ? "Contact form" : "Tenant support"}
            {viewerType === "admin" && <span className="break-all"> · {request.email}</span>}
          </div>
        </div>
        <Badge text={status.toUpperCase()} color={status === "closed" ? "green" : "yellow"} />
        {viewerType === "admin" && onStatusChange && (
          <button
            type="button"
            onClick={() => onStatusChange(status === "closed" ? "OPEN" : "CLOSED")}
            disabled={statusChanging}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {status === "closed" ? <RotateCcw size={13} /> : <CheckCircle2 size={13} />}
            <span className="hidden sm:inline">{status === "closed" ? "Reopen" : "Close chat"}</span>
          </button>
        )}
      </div>

      <div ref={threadRef} onScroll={() => {
        const thread = threadRef.current;
        if (thread) nearBottomRef.current = thread.scrollHeight - thread.scrollTop - thread.clientHeight < 80;
      }} role="log" aria-label="Support conversation" aria-live="polite" className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain bg-gray-50 px-3 py-3 dark:bg-gray-900/50 sm:px-4">
        {messages.map((message, index) => {
          const own = message.authorType === viewerType;
          const previous = index > 0 ? messages[index - 1] : null;
          const day = formatDateTime(message.createdAt, "DD MMM YYYY");
          const previousDay = previous ? formatDateTime(previous.createdAt, "DD MMM YYYY") : null;
          const isUnreadStart = index === firstUnreadIndex;
          // Group consecutive same-author messages like a normal chat app —
          // only the first bubble in a run shows the sender label.
          const grouped = !isUnreadStart && previous?.authorType === message.authorType && previousDay === day;
          return <React.Fragment key={`${message.createdAt}-${index}`}>
            {day && day !== previousDay && <div className="flex justify-center py-1.5"><span className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">{day}</span></div>}
            {isUnreadStart && (
              <div ref={unreadRef} role="separator" aria-label="Unread messages" className="flex items-center gap-3 py-1.5">
                <div className="h-px flex-1 bg-primary-200 dark:bg-primary-800" />
                <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-200">Unread messages</span>
                <div className="h-px flex-1 bg-primary-200 dark:bg-primary-800" />
              </div>
            )}
            <div className={`flex ${own ? "justify-end" : "justify-start"} ${grouped ? "mt-0.5" : "mt-2"}`}>
              <div className={`max-w-[80%] rounded-xl px-3 py-1.5 shadow-sm sm:max-w-[65%] ${own ? "rounded-br-sm bg-primary-600 text-white" : "rounded-bl-sm border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"}`}>
                {!grouped && <div className={`mb-0.5 text-[11px] font-medium ${own ? "text-white/75" : "text-gray-500 dark:text-gray-400"}`}>{own ? "You" : message.authorType === "admin" ? "Invyntra support" : message.authorName}</div>}
                <p className="whitespace-pre-wrap break-words text-[13px] leading-snug [overflow-wrap:anywhere]">{message.message}</p>
                <div className={`mt-0.5 flex items-center justify-end gap-1 text-[10px] ${own ? "text-white/75" : "text-gray-400"}`}>
                  <time dateTime={message.createdAt} title={formatDateTime(message.createdAt, "DD MMM YYYY, hh:mm A") || undefined}>{formatDateTime(message.createdAt, "custom", "hh:mm A")}</time>{own && <Check size={11} aria-label="Saved" />}
                </div>
              </div>
            </div>
          </React.Fragment>;
        })}
        {pending && <div className="mt-2 flex justify-end"><div className="max-w-[80%] rounded-xl rounded-br-sm bg-primary-600 px-3 py-1.5 text-white shadow-sm sm:max-w-[65%]">
          <div className="mb-0.5 text-[11px] font-medium text-white/75">You</div>
          <p className="whitespace-pre-wrap break-words text-[13px] leading-snug [overflow-wrap:anywhere]">{pending.message}</p>
          <div role="status" className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-white/80">{pending.failed ? <><span>Couldn’t {internalReply ? "save" : "send"}.</span><button type="button" onClick={() => send(pending.message)} className="font-semibold underline" disabled={replying}>Retry</button></> : <><Loader2 size={11} className="animate-spin" />{internalReply ? "Saving…" : "Sending…"}</>}</div>
        </div></div>}
      </div>

      {onReply && <form onSubmit={(event) => { event.preventDefault(); send(draft); }} className="shrink-0 border-t border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800 sm:px-4">
        {internalReply && <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Internal conversation · Replies are saved here and are not sent to the requester.</p>}
        <div className="flex items-end gap-2 rounded-2xl border border-gray-300 bg-gray-50 p-1.5 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500 dark:border-gray-600 dark:bg-gray-900/50">
          <textarea
            ref={inputRef}
            aria-label="Reply message"
            rows={1}
            placeholder={internalReply ? "Write an internal reply…" : "Write a message…"}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); send(draft); } }}
            style={{ maxHeight: COMPOSER_MAX_HEIGHT }}
            className="flex-1 resize-none overflow-y-auto border-0 bg-transparent px-2 py-2 text-sm leading-snug outline-none placeholder:text-gray-400"
          />
          <button type="submit" aria-label={internalReply ? "Save reply" : "Send"} disabled={!draft.trim() || replying || Boolean(pending && !pending.failed)} className="flex h-9 shrink-0 items-center gap-2 rounded-xl bg-primary-600 px-3 text-sm font-medium text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"><Send size={15} /><span className="hidden sm:inline">{internalReply ? "Save reply" : "Send"}</span><span className="sr-only sm:hidden">{internalReply ? "Save reply" : "Send"}</span></button>
        </div>
        <p className="mt-1.5 hidden text-[11px] text-gray-400 sm:block">Enter to {internalReply ? "save" : "send"} · Shift + Enter for a new line</p>
      </form>}
    </div>
  );
};

export default MailContent;
